function getSingleParam<TParam = unknown>(params: [TParam] | Record<string, TParam>, paramName: string) {
  return Array.isArray(params) ? params[0] : params[paramName];
}

function isNullOrUndefined(value: unknown): value is undefined | null {
  return value === null || value === undefined;
}

function isEmptyArray(arr: unknown): boolean {
  return Array.isArray(arr) && arr.length === 0;
}

function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined || value === "") {
    return true;
  }

  if (Array.isArray(value) && value.length === 0) {
    return true;
  }

  return false;
}

function requiredValidator(value: unknown) {
  if (isNullOrUndefined(value) || isEmptyArray(value) || value === false) {
    return false;
  }

  return !!String(value).trim().length;
}

function emailValidator(value: unknown) {
  if (isEmpty(value)) {
    return true;
  }

  const re

      = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\])|(([a-z\-0-9]+\.)+[a-z]{2,}))$/i;

  if (Array.isArray(value)) {
    return value.every(val => re.test(String(val)));
  }

  return re.test(String(value));
}

function urlValidator(value: unknown) {
  if (isEmpty(value)) {
    return true;
  }

  const re = /^https?:\/\/(?:www\.)?[-\w@:%.+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b[-\w()@:%+.~#?&/=]*$/;

  if (Array.isArray(value)) {
    return value.every(val => re.test(String(val)));
  }

  return re.test(String(value));
}

function hexColorValidator(value: unknown) {
  if (isEmpty(value)) {
    return true;
  }

  const re = /^#[a-f0-9]{6}$/i;

  return re.test(String(value));
}

function minValidator(value: unknown, params: [string | number] | { length: string | number }): boolean {
  if (isEmpty(value)) {
    return true;
  }

  const length = getSingleParam(params, "length");
  if (Array.isArray(value)) {
    return value.every(val => minValidator(val, { length }));
  }

  return [...String(value)].length >= Number(length);
}

function maxValidator(value: unknown, params: [string | number] | { length: string | number }): boolean {
  if (isEmpty(value)) {
    return true;
  }

  const length = getSingleParam(params, "length");
  if (Array.isArray(value)) {
    return value.every(val => maxValidator(val, { length }));
  }

  return [...String(value)].length <= Number(length);
}

function phoneValidator(value: unknown): boolean {
  if (isEmpty(value)) {
    return true;
  }
  const digitsOnly = String(value).replace(/\D/g, "");
  return /^\d+$/.test(digitsOnly) && digitsOnly.length >= 4 && digitsOnly.length <= 15;
}

function dateOfBirthValidator(day: string, month: string, year: string): boolean {
  if (!day || !month || !year) {
    return false;
  }

  const currentYear = new Date().getFullYear();
  const minYear = currentYear - 120;

  const d = Number.parseInt(day);
  const m = Number.parseInt(month);
  const y = Number.parseInt(year);

  if (m < 1 || m > 12) {
    return false;
  }

  if (d < 1 || d > 31) {
    return false;
  }

  if (y < minYear || y > currentYear) {
    return false;
  }

  const date = new Date(y, m - 1, d);
  return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
}

const validate = {
  email: emailValidator,
  required: requiredValidator,
  min: minValidator,
  max: maxValidator,
  hex: hexColorValidator,
  url: urlValidator,
  phone: phoneValidator,
  dateOfBirth: dateOfBirthValidator,
};

export default validate;
